
import {Component, Vue, Prop, Watch} from "vue-property-decorator";
import QrCodeReader from "@/components/QrCodeReader.vue";
import SimpleDialog from "@/components/SimpleDialog.vue";
import {Getter} from "vuex-class";
import RelaySelectConnectedBox from "@/components/relays/RelaySelectConnectedBox.vue";
import RelaySelectLocationSize from "@/components/relays/RelaySelectLocationSize.vue";
import Timeout = NodeJS.Timeout;
import { CONNECTED_BOX_PROVIDERS } from "@/utils/connectedBoxes";
import QrcodeVue from "qrcode.vue";
const masterlockWakeUpImage = require("@/assets/images/masterlock-wake-up.png");

const REFRESH_TIME_MILLI: number = 2000;
const CLOSE_COUNTDOWN: number = 30;

@Component({
    components: {
        RelaySelectConnectedBox,
        RelaySelectLocationSize,
        QrCodeReader,
        SimpleDialog,
        QrcodeVue,
    },
})export default class KeyDropConnected extends Vue {


    get relay(): any {
        return this.currentKey?.Relays?.find((r) => r.type === "AGENCY");
    }

    get selectedConnectedBox(): any {
        return this.connectedBoxes
          .map((b) => {
             return {...b.ConnectedBox, name: b.name };
        }).find((cb) => cb.id === this.selectedConnectedBoxId);
    }

    get displaySimulateButton(): boolean {
      return this.mylobyEnv !== "production";
    }

    get selectedConnectedBoxLocations(): any[] {
      return this.availableConnectedBoxes
        .find((b) => b.value === this.selectedConnectedBoxId)
        .locations;
    }

    get isSnapaccess(): boolean {
        return this.selectedConnectedBox?.provider === this.connectedBoxesProviders.SNAPACCESS;
    }

    get currentLocation() {
        return this.currentKey?.KeyLocation?.find((kL) => !kL.endDate)?.Location;
    }


    get lang(): string {
        return this.$root.$i18n.locale;
    }

    get displayConfirm() {
        const newTags: boolean = this.mapIds(this.currentKey?.Tags)?.
        some((tagId) => !this.mapIds(this.oldTags)?.includes(tagId));
        const removedTags: boolean = this.mapIds(this.oldTags)?.
        some((tagId) => !this.mapIds(this.currentKey?.Tags)?.includes(tagId));
        return newTags || removedTags;
    }

    @Prop({default: {}})
    public currentKey: any;

    @Prop({default: false})
    public disabled: boolean;

    @Prop({default: false})
    public listView: boolean;

    @Getter private currentUser: any;
    @Getter private isAdmin: boolean;
    @Getter private isMobile: boolean;
    @Getter private mylobyEnv: string;

    private connectedCode: {
      code: string,
      id: number,
    } = null;
    private step1Modal: boolean = false;
    private step2Modal: boolean = false;
    private step3Modal: boolean = false;
    private isLoading: boolean = false;
    private loadingSimulate: boolean = false;
    private displayLockerOpenMessage: boolean = false;
    private lockerNotOpenConfirmation: boolean = false;
    private confirmLockerNotOpen: boolean = false;
    private displayTagAddition: boolean = false;
    private skipped: boolean = false;
    private searchTags: string = null;
    private tags: any = [];
    private oldTags: any = [];
    private intervalId: Timeout = null;
    private connectedBoxes: any[] = [];
    private availableConnectedBoxes: any[] = [];
    private loadingAvailableConnectedBoxes: boolean = false;
    private selectedConnectedBoxId: any = null;
    private displaySelectConnectedBoxModal: boolean = false;
    private locationSizes: any[] = null;
    private selectedSizeId: any = null;
    private displaySelectLocationSizeModal: boolean = false;
    private connectedBoxesProviders = CONNECTED_BOX_PROVIDERS;
    private canDropConnected: boolean = false;
    private displayDeviceNotReadyHelp: boolean = false;
    private masterlockWakeUpImage = masterlockWakeUpImage;
    private  canAddTagAtDrop: boolean = false;

    private countdown: number = CLOSE_COUNTDOWN;
    private countdownInterval: ReturnType<typeof setInterval> | null = null;

    @Watch("currentKey")
    public handlerKeyChange(newVal, oldValue) {
      if (newVal && (newVal.id !== oldValue?.id)) {
        this.fetchRelayOptions();
      }
    }

    @Watch("currentKey.status")
    public handlerKeyStatusChange(newValue, oldValue) {
      if (newValue && (newValue !== oldValue)) {
        this.fetchCanDoAction();
      }
    }

    private async fetchRelayOptions() {
      if (this.relay) {
        this.canAddTagAtDrop = await this.$store.dispatch("options/readOptionValueByRelay", {
          relayId: this.relay.id,
          optionTypeLabel: "canAddTagAtDrop",
        });
      }
    }

    private async fetchCanDoAction() {
      if (this.currentKey.status !== "IN_RELAY") {
        this.$store.dispatch("keys/canDoActions", {
          id: this.currentKey.id,
          actions: [{ type: "CAN_DROP_CONNECTED" }],
        }).then((actions) => this.canDropConnected = actions?.[0]?.status);
      } else {
        this.canDropConnected = false;
      }
    }

    private mounted(): void {
        if (this.currentKey?.Tags) {
            this.oldTags = [...this.currentKey?.Tags];
        }
        if (this.currentKey) {
          this.fetchRelayOptions();
          this.fetchCanDoAction();
        }
    }

    private startCountdown(): void {
        if (this.countdownInterval) {
            return;
        }
        this.countdown = CLOSE_COUNTDOWN;

        this.countdownInterval = setInterval(() => {
            if (this.countdown > 0) {
                this.countdown--;
            }
        }, 1000);
    }

    private stopCountdown(): void {
        if (this.countdownInterval) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
        }
    }

    private formatConnectedBoxName(connectedBox): string {
      return this.$options.filters.formatConnectedBoxName(connectedBox);
    }

    private isLocationOccupied = (l): boolean => l.KeyLocation.length;

    private isLocationReservedForOtherKey(l): boolean {
      return l.keyId && l.keyId !== this.currentKey.id;
    }

    private isLocationAvailable = (l) => !this.isLocationOccupied(l) && !this.isLocationReservedForOtherKey(l);

    private formatAvailableConnectedBoxes(boxes: any[]): any[] {
      // A box is considered occupied if there is an active KeyLocation or a key reservation for every location
      const isBoxInactive = (b): boolean => b.ConnectedBox.provider === this.connectedBoxesProviders.SNAPACCESS
        && !b.BoxActivations.find((a) => !a.endDate);
      const isBoxOccupied = (b): boolean => b.Locations.every((l) => !this.isLocationAvailable(l));
      const getDisabledReason = (b): string|null => {
        if (isBoxInactive(b)) { return "inactive"; }
        if (b.Locations.length === 1) {
          const location = b.Locations[0];
          if (this.isLocationReservedForOtherKey(location)) { return "reserved"; }
          if (this.isLocationOccupied(location)) { return "oneOccupied"; }
        }
        if (isBoxOccupied(b)) { return "allOccupied"; }
        return null;
      };
      return boxes.map((box) => ({
        from: box.from,
        buildingName: box.buildingName,
        value: box.ConnectedBox.id,
        text: box.name || this.formatConnectedBoxName(box.ConnectedBox),
        disabled: getDisabledReason(box),
        locations: box.Locations,
        externalId: box.ConnectedBox.externalId,
        provider: box.ConnectedBox.provider,
      }));
    }

    private async getAvailableConnectedBoxes(): Promise<void> {
      this.loadingAvailableConnectedBoxes = true;
      if (!this.relay?.Building?.id) {
        throw new Error("No building id");
      }
      const promisesBuildingList = [
        {
          from: "relay",
          name: this.relay.Building.name,
          id: this.relay.Building.id,
        },
      ];
      if (this.currentKey.buildingId) {
        promisesBuildingList.push({
          from: "key",
          name: this.currentKey.Building?.name,
          id: this.currentKey.buildingId,
        });
      }
      const promises = promisesBuildingList
        .map((buildingData) => this.$store.dispatch("buildings/getConnectedBoxes", { id: buildingData.id })
          .then((res) => res?.rows.map((row) => ({
            ...row,
            from: buildingData.from,
            buildingName: buildingData.name,
          })),
        ));
      return Promise.all(promises)
        .then((data) => data.reduce((acc, rows) => acc.concat(rows || []), []))
        .then((boxes) => {
        // Auto-select reserved connected box if it exists
        let reservedLocation = null;
        boxes.forEach((box) => {
          box.Locations.forEach((location) => {
            if (location.keyId === this.currentKey.id) {
              reservedLocation = location;
            }
          });
        });
        if (reservedLocation) {
          boxes = boxes.filter((box) => box.Locations[0].id === reservedLocation.id);
        }
        this.connectedBoxes = boxes;
        this.availableConnectedBoxes = this.formatAvailableConnectedBoxes(boxes);
        // Auto-select if there is only one connected box in the relay
        const availableBoxesCount: number = this.availableConnectedBoxes.filter(({disabled}) => !disabled).length;
        if (boxes.length === 1 && availableBoxesCount === 1) {
          this.selectConnectedBox(boxes[0].ConnectedBox.id);
        } else {
          this.displaySelectConnectedBoxModal = true;
        }
      }).finally(() => {
        this.loadingAvailableConnectedBoxes = false;
      });
    }

    private async selectConnectedBox(connectedBoxId: number): Promise<void> {
      this.selectedConnectedBoxId = connectedBoxId;
      // Get all locations sizes
      const allLocationsSizes = this.selectedConnectedBoxLocations
        .map(({ Size }) => Size)
        .filter(Boolean);
      // If no location has a specific size, continue
      if (allLocationsSizes.length === 0) {
        this.step1Modal = true;
        this.displaySelectConnectedBoxModal = false;
        return this.dropOnConnected();
      }
      // Process only different sizes
      this.locationSizes = [
        ...new Set(allLocationsSizes.map((s) => JSON.stringify(s))),
      ].map((s) => JSON.parse(s));
      // Add flag on unavailable sizes
      this.locationSizes.forEach((s) => {
        s.disabled = this.selectedConnectedBoxLocations
          .filter((l) => l.Size?.id === s.id)
          .every((l) => !this.isLocationAvailable(l));
      });
      // Auto-select if there is only one size
      const availableSizesCount: number = this.locationSizes.filter(({disabled}) => !disabled).length;
      if (this.locationSizes.length === 1 && availableSizesCount === 1) {
        return this.selectLocationSize(this.locationSizes[0].id);
      } else {
        this.displaySelectLocationSizeModal = true;
      }
      this.displaySelectConnectedBoxModal = false;
    }

    private async selectLocationSize(sizeId: number): Promise<void> {
      this.selectedSizeId = sizeId;
      this.displaySelectLocationSizeModal = false;
      await this.dropOnConnected();
    }

    private async dropOnConnected(askNewCode: boolean = false): Promise<void> {
        clearInterval(this.intervalId);
        if (!this.selectedConnectedBoxId) {
            this.$store.commit("alerts/displayError", {
                icon: "icon-picto_depot-agence",
                msg: this.$i18n?.t("key.actions.connectedBoxNotExist"),
            });
            return;
        }
        if (!this.skipped) {
          await this.updateKeyTags();
        }
        this.connectedCode = null;
        this.isLoading = true;
        this.step1Modal = true;
        this.displayDeviceNotReadyHelp = false;
        await this.$store.dispatch("keys/dropOnConnected", {
            keyId: this.currentKey.id,
            transferService: this.selectedConnectedBox.provider,
            askNewCode,
            connectedBoxId: this.selectedConnectedBoxId,
            sizeId: this.selectedSizeId,
            holderId: this.currentUser.id,
            referentId: this.isAdmin ? this.currentUser.id : null,
            relayId: this.relay.id,
        }).then((res) => {
            this.connectedCode = {
              id: res.connectedCodeId,
              code: res.connectedCodeCode,
            };
            this.intervalId = setInterval(() => this.checkConnectedKeyMove(), REFRESH_TIME_MILLI);
      }).catch((error) => {
        if (error?.response?.data?.name === "BOX_NOT_READY") {
          this.displayDeviceNotReadyHelp = true;
          this.isLoading = false;
        } else {
          this.$store.commit("alerts/displayHttpError", {
            error,
            icon: "icon-picto_depot-agence",
          });
          this.closeModals();
        }
      });
    }

    private updateKeyTags() {
      return this.$store
        .dispatch("keys/updateById", {
          id: this.currentKey.id,
          data: {
            ...this.currentKey,
            newTags: this.currentKey.Tags,
            Tags: this.mapIds(this.currentKey.Tags),
          },
        });
    }

    private checkConnectedKeyMove(): Promise<void> {
        return this.$store.dispatch("keys/getConnectedKeyMove", {
            id: this.currentKey.id,
            connectedCodeId: this.connectedCode.id,
        }).then((data) => {
            if (data?.step?.name === "WAITING_CLOSE_LOCK") {
                // Code has been used => go to the second step of the workflow
                this.startCountdown();
                this.displayLockerOpenMessage = true;
            } else if (data?.step?.name === "CANCELLED" || !data) {
              // ConnectedKeyMove has been cancelled or automatically deleted
              this.stopCountdown();
              this.errorCallback(this.$i18n?.t(`alerts.error.${data?.step?.message || "transactionExpired"}`));
            } else if (data?.step?.name === "COMPLETED") {
                this.$store.commit("alerts/displaySuccess", {
                    icon: "icon-picto_depot-agence",
                    msg: this.$i18n?.t("alerts.key.dropSuccessInBox", {
                        key: this.currentKey.name,
                    }),
                });
                this.$store.dispatch("context/clearContext");
                this.reset();
            }
        }).catch((err) => {
            this.errorCallback(err);
        });
    }

    private async simulateCodeUse(): Promise<void> {
        this.loadingSimulate = true;
        await this.$store.dispatch("connectorConnectedBoxes/simulateCodeUse", {
            externalId: this.selectedConnectedBox.externalId,
            code: this.connectedCode.code,
        }).then(() => {
            this.$store.commit("alerts/displaySuccess", {
              msg: this.$t("key.actions.connectedBox.simulateAction"),
            });
        }).catch((error) => {
            this.$store.commit("alerts/displayHttpError", { error });
        }).finally(() => {
            this.loadingSimulate = false;
        });
    }

    private cancel() {
      this.closeModals();
    }

    private lockerNotOpen() {
        this.$store.dispatch("keys/lockerNotOpen", {
            keyId: this.currentKey.id,
            connectedCodeId: this.connectedCode.id,
        }).then(() => {
            this.reset();
            this.$store.commit("alerts/displaySuccess", {
                icon: "icon-picto_depot-agence",
                msg: this.$i18n?.t("common.emailSent", {
                    key: this.currentKey.name,
                    relay: this.relay.name,
                }),
            });
        });
    }

    private lockerIsNotOpenConfirmation(): void {
        this.lockerNotOpenConfirmation = true;
    }

    // if user skips adding tags , updating tags method won't be called
    private skip(skipped: boolean): void {
        this.displayTagAddition = false;
        this.skipped = skipped;
        this.getAvailableConnectedBoxes();
    }

    private removeDuplicates(completeList, duplicates): any[] | null {
        if (!completeList || !duplicates) {
            return null;
        }
        return completeList.filter(
            (item: any) =>
                !duplicates.map((elem: any) => elem?.id)?.includes(item?.id),
        );
    }

    private getTags(): void {
        this.$store
            .dispatch("tags/getAll", {
                query: {
                    search: this.searchTags,
                    lang: this.lang,
                },
            })
            .then((res) => {
                this.tags = res;
            });
    }

    private remove(list, item): void {
        const pos = list.map((u) => u.id).indexOf(item.id);
        list.splice(pos, 1);
    }

    private mapIds = (array) => array?.map((item) => item.id) || [];

    private errorCallback(msg: any) {
        this.$store.commit("alerts/displayError", { msg });
        this.reset();
    }

    private reset(): void {
        this.closeModals();
        this.$emit("reset");
    }

    private closeModals(): void {
        // Close modals
        this.displayTagAddition = false;
        this.step1Modal = false;
        this.step2Modal = false;
        this.step3Modal = false;
        this.displayLockerOpenMessage = false;
        this.lockerNotOpenConfirmation = false;
        this.displaySelectConnectedBoxModal = false;
        this.displaySelectLocationSizeModal = false;
        // Reset updated values
        this.isLoading = false;
        this.availableConnectedBoxes = null;
        this.selectedConnectedBoxId = null;
        this.locationSizes = null;
        this.selectedSizeId = null;
        this.connectedCode = null;
        this.stopCountdown();
        clearInterval(this.intervalId);
    }

    private beforeDestroy(): void {
        clearInterval(this.intervalId);
    }

    private nextStep(): void {
        if (this.canAddTagAtDrop) {
            this.getTags();
            this.displayTagAddition = true;
        } else {
            this.getAvailableConnectedBoxes();
        }
    }
}
